import { API_BASE_URL } from './config';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MapContainer as LeafletMapContainer, TileLayer, LayersControl, Marker, Popup, Circle, Polygon, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import secureStorage from './utils/secureStorage';

const API = API_BASE_URL;

// ICS Standard Marker Icons - Complete list matching main mapping component
const ICS_ICONS = {
  // Basic Markers
  'default': { label: 'Default Pin', emoji: '📍', unicode: '\u{1F4CD}' },
  'info': { label: 'Information', emoji: 'ℹ️', unicode: '\u{2139}' },
  'warning': { label: 'Warning', emoji: '⚠️', unicode: '\u{26A0}' },
  'alert': { label: 'Alert', emoji: '🔔', unicode: '\u{1F514}' },
  
  // Command & Control
  'command': { label: 'Command Post', emoji: '⭐', unicode: '\u{2B50}' },
  'incident': { label: 'Incident Base', emoji: '🏢', unicode: '\u{1F3E2}' },
  'staging': { label: 'Staging Area', emoji: '🅿️', unicode: '\u{1F17F}' },
  'operations': { label: 'Operations', emoji: '⚙️', unicode: '\u{2699}' },
  
  // Emergency Services
  'fire': { label: 'Fire Station/Unit', emoji: '🚒', unicode: '\u{1F692}' },
  'police': { label: 'Police', emoji: '🚓', unicode: '\u{1F693}' },
  'ambulance': { label: 'Ambulance', emoji: '🚑', unicode: '\u{1F691}' },
  'rescue': { label: 'Search & Rescue', emoji: '⛑️', unicode: '\u{26D1}' },
  
  // Medical & Health
  'medical': { label: 'Medical Facility', emoji: '🏥', unicode: '\u{1F3E5}' },
  'hospital': { label: 'Hospital', emoji: '🏥', unicode: '\u{1F3E5}' },
  'triage': { label: 'Triage Area', emoji: '🩺', unicode: '\u{1FA7A}' },
  'first-aid': { label: 'First Aid', emoji: '🩹', unicode: '\u{1FA79}' },
  'pharmacy': { label: 'Pharmacy', emoji: '💊', unicode: '\u{1F48A}' },
  
  // Transportation
  'helicopter': { label: 'Helicopter/Helipad', emoji: '🚁', unicode: '\u{1F681}' },
  'airport': { label: 'Airport', emoji: '✈️', unicode: '\u{2708}' },
  'boat': { label: 'Marine/Boat', emoji: '🚤', unicode: '\u{1F6A4}' },
  'vehicle': { label: 'Vehicle', emoji: '🚐', unicode: '\u{1F690}' },
  'truck': { label: 'Truck', emoji: '🚚', unicode: '\u{1F69A}' },
  
  // Hazards & Risks
  'hazmat': { label: 'Hazmat', emoji: '☢️', unicode: '\u{2622}' },
  'fire-hazard': { label: 'Fire Hazard', emoji: '🔥', unicode: '\u{1F525}' },
  'flood': { label: 'Flood Zone', emoji: '🌊', unicode: '\u{1F30A}' },
  'lightning': { label: 'Lightning/Storm', emoji: '⚡', unicode: '\u{26A1}' },
  'tornado': { label: 'Tornado', emoji: '🌪️', unicode: '\u{1F32A}' },
  'earthquake': { label: 'Earthquake', emoji: '🏚️', unicode: '\u{1F3DA}' },
  'landslide': { label: 'Landslide', emoji: '⛰️', unicode: '\u{26F0}' },
  
  // Facilities & Shelters
  'shelter': { label: 'Emergency Shelter', emoji: '🏠', unicode: '\u{1F3E0}' },
  'evacuation': { label: 'Evacuation Center', emoji: '🏃', unicode: '\u{1F3C3}' },
  'school': { label: 'School', emoji: '🏫', unicode: '\u{1F3EB}' },
  'building': { label: 'Building', emoji: '🏢', unicode: '\u{1F3E2}' },
  'tent': { label: 'Tent/Temporary', emoji: '⛺', unicode: '\u{26FA}' },
  
  // Resources & Supplies
  'supply': { label: 'Supply Point', emoji: '📦', unicode: '\u{1F4E6}' },
  'food': { label: 'Food Distribution', emoji: '🍽️', unicode: '\u{1F37D}' },
  'water': { label: 'Water Supply', emoji: '💧', unicode: '\u{1F4A7}' },
  'fuel': { label: 'Fuel Station', emoji: '⛽', unicode: '\u{26FD}' },
  'generator': { label: 'Generator/Power', emoji: '🔌', unicode: '\u{1F50C}' },
  
  // Safety & Security
  'security': { label: 'Security', emoji: '🔒', unicode: '\u{1F512}' },
  'checkpoint': { label: 'Checkpoint', emoji: '🛑', unicode: '\u{1F6D1}' },
  'roadblock': { label: 'Road Block', emoji: '🚧', unicode: '\u{1F6A7}' },
  'barrier': { label: 'Barrier', emoji: '🚧', unicode: '\u{1F6A7}' },
  
  // Communications
  'communications': { label: 'Communications', emoji: '📡', unicode: '\u{1F4E1}' },
  'radio': { label: 'Radio Tower', emoji: '📻', unicode: '\u{1F4FB}' },
  'phone': { label: 'Phone', emoji: '☎️', unicode: '\u{260E}' },
  'wifi': { label: 'WiFi Point', emoji: '📶', unicode: '\u{1F4F6}' },
  
  // Personnel & Teams
  'personnel': { label: 'Personnel', emoji: '👤', unicode: '\u{1F464}' },
  'team': { label: 'Team', emoji: '👥', unicode: '\u{1F465}' },
  'volunteer': { label: 'Volunteers', emoji: '🙋', unicode: '\u{1F64B}' },
  
  // Special Operations
  'drone': { label: 'Drone/UAV', emoji: '🛸', unicode: '\u{1F6F8}' },
  'camera': { label: 'Surveillance', emoji: '📹', unicode: '\u{1F4F9}' },
  'laboratory': { label: 'Laboratory', emoji: '🔬', unicode: '\u{1F52C}' },
  'decon': { label: 'Decontamination', emoji: '🚿', unicode: '\u{1F6BF}' },
  
  // Utilities & Infrastructure
  'electrical': { label: 'Electrical', emoji: '⚡', unicode: '\u{26A1}' },
  'water-utility': { label: 'Water Utility', emoji: '🚰', unicode: '\u{1F6B0}' },
  'bridge': { label: 'Bridge', emoji: '🌉', unicode: '\u{1F309}' },
  'road': { label: 'Road/Route', emoji: '🛣️', unicode: '\u{1F6E3}' },
  
  // Weather & Environment
  'weather': { label: 'Weather Station', emoji: '🌡️', unicode: '\u{1F321}' },
  'wind': { label: 'Wind', emoji: '💨', unicode: '\u{1F4A8}' },
  'snow': { label: 'Snow', emoji: '❄️', unicode: '\u{2744}' },
  'sun': { label: 'Heat/Sun', emoji: '☀️', unicode: '\u{2600}' },
  
  // Additional Emergency Icons
  'casualties': { label: 'Casualties', emoji: '🚨', unicode: '\u{1F6A8}' },
  'morgue': { label: 'Morgue', emoji: '⚰️', unicode: '\u{26B0}' },
  'pet-shelter': { label: 'Pet Shelter', emoji: '🐕', unicode: '\u{1F415}' },
  'sanitation': { label: 'Sanitation', emoji: '🚽', unicode: '\u{1F6BD}' },
  'lost-found': { label: 'Lost & Found', emoji: '🔍', unicode: '\u{1F50D}' },
  'meeting': { label: 'Meeting Point', emoji: '📍', unicode: '\u{1F4CD}' }
};

// Map layer options
const MAP_LAYERS = {
  satellite: {
    name: '🛰️ Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '© ESRI'
  },
  streets: {
    name: '🗺️ Streets',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors'
  },
  topographical: {
    name: '⛰️ Topographical',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: 'Map data: © OpenStreetMap contributors, SRTM | Map style: © OpenTopoMap (CC-BY-SA)'
  },
  greyscale: {
    name: '🏙️ Greyscale',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '© CartoDB'
  }
};

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Fit the map to include every map object on load (markers, polygons,
// circles, rectangles, lines). Runs once whenever the object list changes
// and there is at least one valid object.
const FitBoundsToObjects = ({ objects }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !Array.isArray(objects) || objects.length === 0) return;
    const points = [];
    for (const obj of objects) {
      const geom = obj?.geometry;
      if (!geom || !geom.coordinates) continue;
      const t = obj.type;
      try {
        if (t === 'marker') {
          const [lng, lat] = geom.coordinates;
          if (Number.isFinite(lat) && Number.isFinite(lng)) points.push([lat, lng]);
        } else if (t === 'circle') {
          const [lng, lat] = geom.coordinates;
          const r = Number(obj.radius) || 0;
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            // Approximate circle bounds by expanding lat/lng by radius (meters).
            const dLat = r / 111320; // ~ meters per degree latitude
            const dLng = r / (111320 * Math.cos((lat * Math.PI) / 180 || 0.0001));
            points.push([lat - dLat, lng - dLng]);
            points.push([lat + dLat, lng + dLng]);
          }
        } else if ((t === 'polygon' || t === 'rectangle') && Array.isArray(geom.coordinates)) {
          const ring = geom.coordinates[0];
          if (Array.isArray(ring)) {
            for (const c of ring) {
              if (Array.isArray(c) && Number.isFinite(c[0]) && Number.isFinite(c[1])) {
                points.push([c[1], c[0]]);
              }
            }
          }
        } else if (t === 'line' && Array.isArray(geom.coordinates)) {
          for (const c of geom.coordinates) {
            if (Array.isArray(c) && Number.isFinite(c[0]) && Number.isFinite(c[1])) {
              points.push([c[1], c[0]]);
            }
          }
        }
      } catch (e) {
        // Ignore malformed object.
      }
    }
    if (points.length === 0) return;
    try {
      const bounds = L.latLngBounds(points);
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
      }
    } catch (e) {
      console.warn('FitBoundsToObjects: unable to fit bounds', e);
    }
  }, [map, objects]);
  return null;
};

const DisplayMapping = () => {
  const [exerciseName, setExerciseName] = useState('');
  const [exerciseId, setExerciseId] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [mapObjects, setMapObjects] = useState([]);
  
  // Get saved map position and zoom from localStorage, or use defaults
  const getSavedMapView = () => {
    const savedLat = secureStorage.getPreference('map-center-lat');
    const savedLng = secureStorage.getPreference('map-center-lng');
    const savedZoom = secureStorage.getPreference('map-zoom');
    
    if (savedLat && savedLng && savedZoom) {
      return {
        center: [parseFloat(savedLat), parseFloat(savedLng)],
        zoom: parseInt(savedZoom, 10)
      };
    }
    
    return {
      center: [45.4215, -75.6972], // Default to Ottawa
      zoom: 10
    };
  };
  
  const initialView = getSavedMapView();
  const [mapCenter, setMapCenter] = useState(initialView.center);
  const [mapZoom, setMapZoom] = useState(initialView.zoom);

  const loadMapData = async () => {
    try {
      const token = secureStorage.getToken();
      
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // 1) Prefer the exercise ID passed via the URL hash query string
      //    (e.g. `#display-mapping?exercise=abc123`). This ensures the Display
      //    Mode always loads the exact exercise the user opened, regardless
      //    of any stale value in localStorage.
      let exerciseIdToUse = null;
      let exerciseNameToUse = null;
      try {
        const rawHash = (window.location.hash || '').substring(1);
        const qStr = rawHash.includes('?') ? rawHash.split('?')[1] : '';
        if (qStr) {
          const params = new URLSearchParams(qStr);
          const fromUrl = params.get('exercise') || params.get('exerciseId') || params.get('exercise_id');
          if (fromUrl) exerciseIdToUse = fromUrl;
        }
      } catch (e) {
        console.warn('DisplayMapping: could not parse URL hash query params', e);
      }

      // 2) Fall back to the last-selected exercise stored by the app.
      if (!exerciseIdToUse) {
        exerciseIdToUse = secureStorage.getPreference('current-exercise-id');
        exerciseNameToUse = secureStorage.getPreference('current-exercise-name');
      }
      
      if (!exerciseIdToUse) {
        // 3) Final fallback: current exercise API
        const exerciseResponse = await axios.get(`${API}/api/exercises/current`, { headers });
        if (exerciseResponse.data) {
          exerciseIdToUse = exerciseResponse.data.id;
          exerciseNameToUse = exerciseResponse.data.exercise_name;
        }
      }

      // If we resolved the exercise ID from the URL, fetch its name for the
      // header (best-effort — leave blank on failure).
      if (exerciseIdToUse && !exerciseNameToUse) {
        try {
          const r = await axios.get(`${API}/api/exercise-builder/${exerciseIdToUse}`, { headers });
          exerciseNameToUse = r.data?.exercise_name || '';
        } catch (e) {
          // Ignore — the map will still render without a name.
        }
      }
      
      if (exerciseIdToUse) {
        setExerciseId(exerciseIdToUse);
        setExerciseName(exerciseNameToUse || 'Exercise Map');
        
        // Load map objects
        const response = await axios.get(
          `${API}/api/map-objects?exercise_id=${exerciseIdToUse}`,
          { headers }
        );
        
        if (response.data) {
          console.log('Map objects loaded:', response.data.length);
          setMapObjects(response.data);
        }
      }
      
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading map data:', error);
    }
  };

  useEffect(() => {
    loadMapData();

    // Auto-refresh every 120 seconds
    const refreshInterval = setInterval(() => {
      loadMapData();
    }, 120000);

    return () => clearInterval(refreshInterval);
  }, []);

  const getIconForObjectType = (type) => {
    const iconColors = {
      'incident': 'red',
      'resource': 'blue',
      'facility': 'green',
      'hazard': 'orange',
      'staging': 'purple',
      'default': 'gray'
    };
    
    const color = iconColors[type?.toLowerCase()] || iconColors.default;
    
    return L.divIcon({
      html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white;"></div>`,
      className: 'custom-marker',
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });
  };

  return (
    <div className="w-screen h-screen bg-gray-900 overflow-hidden flex flex-col">
      {/* Header Bar */}
      <div className="bg-gray-800 border-b border-orange-500 px-6 py-4 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-orange-500">EXRSIM - Mapping Display</h1>
          <p className="text-lg text-gray-400">{exerciseName}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-400">Last Updated</p>
          <p className="text-xl text-white">{lastUpdate.toLocaleTimeString()}</p>
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1">
        <LeafletMapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          zoomControl={true}
          zoomSnap={0.1}
          zoomDelta={0.5}
        >
          {/* Default base layer - Satellite */}
          <TileLayer
            attribution='© ESRI'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
          
          {/* Layer control for different map types */}
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="🛰️ Satellite">
              <TileLayer
                attribution='© ESRI'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
            </LayersControl.BaseLayer>
            
            <LayersControl.BaseLayer name="🗺️ Streets">
              <TileLayer
                attribution='© OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            
            <LayersControl.BaseLayer name="⛰️ Topographical">
              <TileLayer
                attribution='Map data: © OpenStreetMap contributors, SRTM | Map style: © OpenTopoMap (CC-BY-SA)'
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            
            <LayersControl.BaseLayer name="🏙️ Greyscale">
              <TileLayer
                attribution='© CartoDB'
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              />
            </LayersControl.BaseLayer>
          </LayersControl>
          
          {/* Auto-fit the viewport to include every object */}
          <FitBoundsToObjects objects={mapObjects} />

          {/* Render map objects */}
          {mapObjects
            .filter((obj) => {
              // Filter out objects with invalid geometry
              if (!obj.geometry || !obj.geometry.coordinates) {
                console.warn('Object missing geometry or coordinates:', obj);
                return false;
              }
              return true;
            })
            .map((obj) => {
            try {
              // Handle markers - GeoJSON format: [lng, lat]
              if (obj.type === 'marker' && obj.geometry?.coordinates) {
                const [lng, lat] = obj.geometry.coordinates;
                
                // Get ICS icon data
                const iconType = obj.icon || 'default';
                const iconData = ICS_ICONS[iconType] || ICS_ICONS['default'];
                const iconColor = obj.color || '#3388ff';
                
                // Create custom DivIcon with colored pin and ICS emoji/icon
                const customIcon = L.divIcon({
                  html: `<div style="
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 45px;
                    height: 45px;
                  ">
                    <div style="
                      position: absolute;
                      bottom: 0;
                      left: 50%;
                      transform: translateX(-50%);
                      width: 36px;
                      height: 36px;
                      background-color: ${iconColor};
                      border-radius: 50% 50% 50% 0;
                      transform: translateX(-50%) rotate(-45deg);
                      border: 3px solid white;
                      box-shadow: 0 2px 8px rgba(0,0,0,0.4);
                    "></div>
                    <div style="
                      position: absolute;
                      bottom: 6px;
                      left: 50%;
                      transform: translateX(-50%);
                      font-size: 20px;
                      z-index: 1;
                      filter: drop-shadow(0 1px 1px rgba(0,0,0,0.3));
                    ">${iconData.emoji}</div>
                  </div>`,
                  className: 'custom-ics-icon',
                  iconSize: [45, 45],
                  iconAnchor: [22, 45],
                  popupAnchor: [0, -45]
                });
                
                return (
                  <Marker
                    key={obj.id}
                    position={[lat, lng]}
                    icon={customIcon}
                  >
                    <Popup>
                      <div style={{ minWidth: '200px', maxWidth: '300px' }}>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold', 
                          marginBottom: '8px',
                          color: '#1f2937'
                        }}>
                          {obj.name || 'Map Object'}
                        </div>
                        
                        {/* Image Display */}
                        {obj.image && (
                          <div style={{ marginBottom: '12px' }}>
                            <img 
                              src={obj.image} 
                              alt={obj.name || 'Map object'}
                              style={{ 
                                width: '100%',
                                maxHeight: '200px',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                border: '2px solid #e5e7eb'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        
                        {obj.description && (
                          <div style={{ 
                            fontSize: '14px', 
                            marginBottom: '8px',
                            color: '#4b5563'
                          }}>
                            {obj.description}
                          </div>
                        )}
                        
                        {/* Coordinates */}
                        <div style={{ 
                          marginTop: '8px',
                          padding: '8px',
                          background: '#f3f4f6',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#374151' }}>
                            Coordinates:
                          </div>
                          <div style={{ color: '#6b7280' }}>
                            Lat: {lat.toFixed(6)}<br/>
                            Lng: {lng.toFixed(6)}
                          </div>
                        </div>
                        
                        {/* Icon & Color Info */}
                        <div style={{ 
                          marginTop: '8px',
                          display: 'flex',
                          gap: '8px',
                          fontSize: '12px'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', color: '#374151' }}>Icon:</div>
                            <div style={{ color: '#6b7280' }}>{iconData.emoji} {iconData.label}</div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', color: '#374151' }}>Color:</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{ 
                                width: '16px', 
                                height: '16px', 
                                backgroundColor: iconColor,
                                border: '1px solid #d1d5db',
                                borderRadius: '2px'
                              }}></div>
                              <span style={{ color: '#6b7280' }}>{iconColor}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              }
              
              // Handle circles - GeoJSON format
              if (obj.type === 'circle' && obj.geometry?.coordinates) {
                const [lng, lat] = obj.geometry.coordinates;
                return (
                  <Circle
                    key={obj.id}
                    center={[lat, lng]}
                    radius={obj.radius || 1000}
                    pathOptions={{ 
                      color: obj.color || '#3388ff',
                      fillColor: obj.color || '#3388ff',
                      fillOpacity: 0.3
                    }}
                  >
                    <Popup>
                      <div style={{ minWidth: '200px', maxWidth: '300px' }}>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold', 
                          marginBottom: '8px',
                          color: '#1f2937'
                        }}>
                          {obj.name || 'Circle'}
                        </div>
                        
                        {/* Image Display */}
                        {obj.image && (
                          <div style={{ marginBottom: '12px' }}>
                            <img 
                              src={obj.image} 
                              alt={obj.name || 'Circle'}
                              style={{ 
                                width: '100%',
                                maxHeight: '200px',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                border: '2px solid #e5e7eb'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        
                        {obj.description && (
                          <div style={{ 
                            fontSize: '14px', 
                            marginBottom: '8px',
                            color: '#4b5563'
                          }}>
                            {obj.description}
                          </div>
                        )}
                        
                        {/* Center Coordinates & Radius */}
                        <div style={{ 
                          marginTop: '8px',
                          padding: '8px',
                          background: '#f3f4f6',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#374151' }}>
                            Center Coordinates:
                          </div>
                          <div style={{ color: '#6b7280', marginBottom: '8px' }}>
                            Lat: {lat.toFixed(6)}<br/>
                            Lng: {lng.toFixed(6)}
                          </div>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#374151' }}>
                            Radius:
                          </div>
                          <div style={{ color: '#6b7280' }}>
                            {(obj.radius || 1000).toFixed(2)} meters
                          </div>
                        </div>
                        
                        {/* Color Info */}
                        <div style={{ marginTop: '8px', fontSize: '12px' }}>
                          <div style={{ fontWeight: 'bold', color: '#374151' }}>Color:</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                            <div style={{ 
                              width: '16px', 
                              height: '16px', 
                              backgroundColor: obj.color || '#3388ff',
                              border: '1px solid #d1d5db',
                              borderRadius: '2px'
                            }}></div>
                            <span style={{ color: '#6b7280' }}>{obj.color || '#3388ff'}</span>
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Circle>
                );
              }
              
              // Handle polygons - GeoJSON format: array of [lng, lat] pairs
              if (obj.type === 'polygon' && obj.geometry?.coordinates && Array.isArray(obj.geometry.coordinates)) {
                // Polygon coordinates are nested: [[lng, lat], [lng, lat], ...]
                const coords = obj.geometry.coordinates[0]; // First ring
                if (coords && Array.isArray(coords)) {
                  const positions = coords.map(coord => [coord[1], coord[0]]); // Convert [lng,lat] to [lat,lng]
                  const firstPoint = coords[0];
                  
                  return (
                    <Polygon
                      key={obj.id}
                      positions={positions}
                      pathOptions={{ 
                        color: obj.color || '#3388ff',
                        fillColor: obj.color || '#3388ff',
                        fillOpacity: 0.3
                      }}
                    >
                      <Popup>
                        <div style={{ minWidth: '200px', maxWidth: '300px' }}>
                          <div style={{ 
                            fontSize: '16px', 
                            fontWeight: 'bold', 
                            marginBottom: '8px',
                            color: '#1f2937'
                          }}>
                            {obj.name || 'Polygon'}
                          </div>
                          
                          {/* Image Display */}
                          {obj.image && (
                            <div style={{ marginBottom: '12px' }}>
                              <img 
                                src={obj.image} 
                                alt={obj.name || 'Polygon'}
                                style={{ 
                                  width: '100%',
                                  maxHeight: '200px',
                                  objectFit: 'cover',
                                  borderRadius: '6px',
                                  border: '2px solid #e5e7eb'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          
                          {obj.description && (
                            <div style={{ 
                              fontSize: '14px', 
                              marginBottom: '8px',
                              color: '#4b5563'
                            }}>
                              {obj.description}
                            </div>
                          )}
                          
                          {/* First Point Coordinates */}
                          <div style={{ 
                            marginTop: '8px',
                            padding: '8px',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#374151' }}>
                              First Point Coordinates:
                            </div>
                            <div style={{ color: '#6b7280' }}>
                              Lat: {firstPoint[1].toFixed(6)}<br/>
                              Lng: {firstPoint[0].toFixed(6)}
                            </div>
                            <div style={{ marginTop: '4px', color: '#9ca3af', fontSize: '11px' }}>
                              ({coords.length} vertices)
                            </div>
                          </div>
                          
                          {/* Color Info */}
                          <div style={{ marginTop: '8px', fontSize: '12px' }}>
                            <div style={{ fontWeight: 'bold', color: '#374151' }}>Color:</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                              <div style={{ 
                                width: '16px', 
                                height: '16px', 
                                backgroundColor: obj.color || '#3388ff',
                                border: '1px solid #d1d5db',
                                borderRadius: '2px'
                              }}></div>
                              <span style={{ color: '#6b7280' }}>{obj.color || '#3388ff'}</span>
                            </div>
                          </div>
                        </div>
                      </Popup>
                    </Polygon>
                  );
                }
              }
              
              // Handle rectangles - GeoJSON format
              if (obj.type === 'rectangle' && obj.geometry?.coordinates && Array.isArray(obj.geometry.coordinates)) {
                const coords = obj.geometry.coordinates[0]; // Rectangle coordinates
                if (coords && Array.isArray(coords)) {
                  const positions = coords.map(coord => [coord[1], coord[0]]); // Convert [lng,lat] to [lat,lng]
                  
                  return (
                    <Polygon
                      key={obj.id}
                      positions={positions}
                      pathOptions={{ 
                        color: obj.color || '#3388ff',
                        fillColor: obj.color || '#3388ff',
                        fillOpacity: 0.3
                      }}
                    >
                      <Popup>
                        <div className="text-sm">
                          <strong>{obj.name || 'Rectangle'}</strong>
                          {obj.description && <p className="mt-1">{obj.description}</p>}
                        </div>
                      </Popup>
                    </Polygon>
                  );
                }
              }
              
              // Handle lines - GeoJSON format
              if (obj.type === 'line' && obj.geometry?.coordinates && Array.isArray(obj.geometry.coordinates)) {
                const positions = obj.geometry.coordinates.map(coord => [coord[1], coord[0]]); // Convert [lng,lat] to [lat,lng]
                
                return (
                  <Polyline
                    key={obj.id}
                    positions={positions}
                    pathOptions={{ 
                      color: obj.color || '#3388ff',
                      weight: 3
                    }}
                  >
                    <Popup>
                      <div className="text-sm">
                        <strong>{obj.name || 'Line'}</strong>
                        {obj.description && <p className="mt-1">{obj.description}</p>}
                      </div>
                    </Popup>
                  </Polyline>
                );
              }
              
              console.warn('Unhandled object type or invalid coordinates:', obj);
              return null;
            } catch (error) {
              console.error('Error rendering map object:', obj, error);
              return null;
            }
          }
        )}
        </LeafletMapContainer>
      </div>
    </div>
  );
};

export default DisplayMapping;
